import React, { useContext, useEffect, useState } from 'react';
import { Modal, TableWithHeader } from 'dyl-components';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import QuotesAndOrdersToolbar from './QuotesAndOrdersToolbar';
import QuotesAndOrdersTable from './QuotesAndOrdersTable';
import { useDispatch, useSelector } from 'react-redux';
import usersActions from 'actions/users';

import './index.scss';
import BulkActionsProvider, { BulkActionsContext } from 'shared/context/BulkActionsProvider';
import { KEY_ACCOUNT_CO_OWNER, KEY_ACCOUNT_NAME, KEY_ACCOUNT_OWNER, KEY_ACCOUNT_STATUS, KEY_CONTACT_CO_OWNER, KEY_CONTACT_NAME, KEY_CONTACT_OWNER, KEY_CREATED, KEY_CREATED_BY, KEY_EXPIRES_ON, KEY_LAST_MODIFIED, KEY_LAST_MODIFIED_BY, KEY_NUMBER_PRODUCTS, KEY_OPPORTUNITY_NAME, KEY_OPPORTUNITY_OUTCOME, KEY_PRODUCTS, KEY_PROPOSAL_INVOICE_CREATED, KEY_QUOTE_ORDER_ID, KEY_STATUS, KEY_SUBTOTAL } from 'shared/constants/QUOTES_ORDERS_COLUMNS';
import quotesAndOrdersActions from 'actions/quotes_and_orders';
import QuoteBuilderProvider from 'shared/context/QuoteBuilderProvider';
import { PhoneUtil } from 'utils';
import useModal from 'utils/useModal';
import NewActivityForm from 'shared/forms/NewActivity';

const QuotesAndOrders = ({ root }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [params] = useSearchParams();

    const columnDisplayOptions = [KEY_CREATED, KEY_CREATED_BY, KEY_LAST_MODIFIED, KEY_LAST_MODIFIED_BY, KEY_PROPOSAL_INVOICE_CREATED, KEY_EXPIRES_ON, KEY_QUOTE_ORDER_ID, KEY_NUMBER_PRODUCTS, KEY_PRODUCTS, KEY_CONTACT_OWNER, KEY_CONTACT_CO_OWNER, KEY_ACCOUNT_OWNER, KEY_ACCOUNT_CO_OWNER, KEY_CONTACT_NAME, KEY_ACCOUNT_NAME, KEY_OPPORTUNITY_NAME, KEY_SUBTOTAL, KEY_STATUS, KEY_ACCOUNT_STATUS, KEY_OPPORTUNITY_OUTCOME];
    const defaultColumnsDisplayed = [KEY_CREATED, KEY_CREATED_BY, KEY_LAST_MODIFIED_BY, KEY_QUOTE_ORDER_ID, KEY_CONTACT_OWNER, KEY_ACCOUNT_OWNER, KEY_CONTACT_NAME, KEY_ACCOUNT_NAME, KEY_STATUS];
    const allowedColumns = [
        KEY_CREATED,
        KEY_CREATED_BY,
        KEY_LAST_MODIFIED,
        KEY_LAST_MODIFIED_BY,
        KEY_PROPOSAL_INVOICE_CREATED,
        KEY_EXPIRES_ON,
        KEY_CONTACT_NAME,
        KEY_ACCOUNT_NAME,
        KEY_OPPORTUNITY_NAME,
        KEY_SUBTOTAL,
        KEY_STATUS
    ]
    const columnsParameter = params.get('columns')?.split(',')?.filter(column => columnDisplayOptions.includes(column)) || [];
    const columns = columnsParameter.length ? columnsParameter : defaultColumnsDisplayed;

    const [, setSelectedQuotesAndOrders, , setAreQuotesAndOrdersInAllPagesSelected] = useContext(BulkActionsContext);

    const [filters, setFilters] = useState({})
    const userPhoneCreds = useSelector((state) => state.officeView.creds.extension);
    const isPhoneSystemSupported = PhoneUtil.isPhoneSystemSupported(userPhoneCreds);
    const callActivityModal = useModal();
    const onCallIconClick = (contact_id) => {
        if (!isPhoneSystemSupported) {
            callActivityModal.openModal(contact_id);
        }
    };

    const onFilter = async (_, { name, value }) => {
        setFilters({
            ...filters,
            [name]: value
        });
    }

    useEffect(() => {
        const query = new URLSearchParams(params);
        const query_string = query.toString();
        navigate(`${root}/quotes-orders${query_string ? `?${query_string}` : ''}`,);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters])

    const onPageChange = (_, { activePage }) => {
        const query = new URLSearchParams(params);
        query.set('page', activePage);
        const query_string = query.toString();
        navigate(`${root}/quotes-orders${query_string ? `?${query_string}` : ''}`);
    }

    const isColumnHidden = (column) => {
        return !columns.includes(column);
    }

    const onToggleColumn = async (column) => {
        const updatedColumns = isColumnHidden(column) ? [...columns, column, KEY_CONTACT_NAME] : columns.filter(visibleColumn => visibleColumn !== column);

        const updateColumnsParameter = (filtersToRemove) => {
            const query = new URLSearchParams(params);
            if (updatedColumns.length) {
                query.set('columns', updatedColumns.join(','));
            } else {
                query.delete('columns');
            }
            if (filtersToRemove) {
                const filters = Array.isArray(filtersToRemove) ? filtersToRemove : [filtersToRemove];
                filters.forEach(f => query.delete(f));
            }
            const query_string = query.toString();
            navigate(`${root}/quotes-orders${query_string ? `?${query_string}` : ''}`);
        }

        if (!isColumnHidden(column)) {
            switch (column) {
                case KEY_CREATED:
                    if (params.get('start') || params.get('end') || params.get('order_by') === 'created') {
                        updateColumnsParameter(['start', 'end', 'order_by', 'order']);
                    } else {
                        updateColumnsParameter();
                    }
                    break;
                case KEY_LAST_MODIFIED:
                    if (params.get('order_by') === 'activity') {
                        updateColumnsParameter(['order_by', 'order']);
                    } else {
                        updateColumnsParameter();
                    }
                    break;
                default:
                    updateColumnsParameter();
            }
        } else {
            updateColumnsParameter();
        }
    }

    const location = useLocation();

    useEffect(() => {
        if (!location.state?.isTogglingPanel) {
            const { status, ...queryParameters } = {
                page: 1,
                limit: 25,
                ...Object.fromEntries(params),
                columns: [...new Set([...columns.filter(column => allowedColumns.includes(column)), "opportunity_id", "account_id", "contact_id", "proposal_invoice_created"])].join(",")
            }
            dispatch(quotesAndOrdersActions.readAll(queryParameters))
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params])

    useEffect(() => {
        if (!location.state?.isTogglingPanel) {
            dispatch(usersActions.readUsers());
            setSelectedQuotesAndOrders([]);
            setAreQuotesAndOrdersInAllPagesSelected(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    return (
        <div className='QuotesAndOrdersPage'>
            <Modal
                open={callActivityModal?.isVisible}
                onClose={callActivityModal?.closeModal}
                size='tiny'
            >
                <Modal.Content>
                    <NewActivityForm
                        onClose={callActivityModal?.closeModal}
                        hideActivitySelection
                        contact={{ id: callActivityModal?.data }}
                        isContact
                    />
                </Modal.Content>
            </Modal>
            <TableWithHeader
                header={(
                    <QuotesAndOrdersToolbar
                        isColumnHidden={isColumnHidden}
                        onToggleColumn={onToggleColumn}
                    />
                )}
                table={(
                    <QuotesAndOrdersTable
                        isColumnHidden={isColumnHidden}
                        onFilter={onFilter}
                        onPageChange={onPageChange}
                        onCallIconClick={onCallIconClick}
                    />
                )}
            />
        </div>
    );
}

export default function QuotesAndOrdersContainer({ root = '/settings' }) {
    const dispatch = useDispatch();
    const [params] = useSearchParams();
    const columnDisplayOptions = [KEY_CREATED, KEY_CREATED_BY, KEY_LAST_MODIFIED, KEY_LAST_MODIFIED_BY, KEY_PROPOSAL_INVOICE_CREATED, KEY_EXPIRES_ON, KEY_QUOTE_ORDER_ID, KEY_CONTACT_OWNER, KEY_CONTACT_CO_OWNER, KEY_ACCOUNT_OWNER, KEY_ACCOUNT_CO_OWNER, KEY_CONTACT_NAME, KEY_ACCOUNT_NAME, KEY_OPPORTUNITY_NAME, KEY_SUBTOTAL, KEY_STATUS, KEY_ACCOUNT_STATUS, KEY_OPPORTUNITY_OUTCOME];
    const defaultColumnsDisplayed = [KEY_CREATED, KEY_CREATED_BY, KEY_LAST_MODIFIED_BY, KEY_QUOTE_ORDER_ID, KEY_CONTACT_OWNER, KEY_ACCOUNT_OWNER, KEY_CONTACT_NAME, KEY_ACCOUNT_NAME, KEY_STATUS];
    const allowedColumns = [
        KEY_CREATED,
        KEY_CREATED_BY,
        KEY_LAST_MODIFIED,
        KEY_LAST_MODIFIED_BY,
        KEY_PROPOSAL_INVOICE_CREATED,
        KEY_EXPIRES_ON,
        KEY_CONTACT_NAME,
        KEY_ACCOUNT_NAME,
        KEY_OPPORTUNITY_NAME,
        KEY_SUBTOTAL,
        KEY_STATUS
    ]
    const columnsParameter = params.get('columns')?.split(',')?.filter(column => columnDisplayOptions.includes(column)) || [];
    const columns = columnsParameter.length ? columnsParameter : defaultColumnsDisplayed;
    return (
        <BulkActionsProvider>
            <QuoteBuilderProvider refreshAccountInformation={() => {
                const { status, ...queryParameters } = {
                    page: 1,
                    limit: 25,
                    ...Object.fromEntries(params),
                    columns: [...new Set([...columns.filter(column => allowedColumns.includes(column)), "opportunity_id", "account_id", "contact_id", "proposal_invoice_created"])].join(",")
                }
                dispatch(quotesAndOrdersActions.readAll(queryParameters))
            }}>
                <QuotesAndOrders root={root} />
            </QuoteBuilderProvider>
        </BulkActionsProvider>
    )
};
